// Mock data for The Goldilocks Zone website

export const bookData = {
  title: "The Goldilocks Zone",
  tagline: "Creating hospitable organisations in inhospitable environments.",
  alternateTagline: "Where meaningful change becomes possible, and sustainable.",
  description: `Her technology team said they were on fire. Her product team said they were frozen solid. Both were right – and that's the problem most leaders don't know how to solve.

Billions are spent on transformation every year. Most of it fails – not because the strategy was wrong or the people weren't talented enough, but because the organisational environment couldn't sustain the change. Leaders design brilliant initiatives, then plant them in hostile soil and wonder why nothing grows.

The Goldilocks Zone offers a different approach. Drawn from decades of leading complex transformations across industries and geographies, Nadir Khan and Craig Kimball reveal why the real work of transformation isn't designing the change – it's managing the environment the change has to survive in.

Using a powerful metaphor from astrophysics, they show how organisations operate in climate zones: too cold and people disengage, innovation stalls, and bureaucracy calcifies. Too hot and teams burn out, politics flare, and every decision becomes an emergency. Real progress happens in the narrow band between – where structure and adaptability coexist, where collective intelligence replaces organisational politics, and where change becomes something people are drawn to rather than subjected to.

This isn't another leadership playbook full of checklists and frameworks. This is the physics behind the machinery – the first principles that enable leaders to diagnose their organisational climate, stabilise it, and create the conditions where transformation thrives on its own.`,
  keyIdeas: [
    "Environmental Management for Leaders: Your job isn't to drive the change – it's to create the conditions where change drives itself.",
    "Collective Intelligence Over Organisational Politics: Why smart organisations still make unintelligent decisions – and how to unlock the wisdom already inside your teams.",
    "Decision-Making Under Complexity: The five thinking distortions that derail transformation – and how to recognise them before they take hold.",
    "The Human Reinforcement System: Alignment, gratitude, inclusion, forgiveness, and trust – the behavioural forces that make transformation something people crave, not endure.",
    "Ecosystems Thinking: Why transformations planted in hostile soil always die – and how to make your organisation a place where change takes root and flourishes.",
    "The Habitability Principle: Why brilliant strategies fail in hostile environments – and how to make your organisation a place where change can survive, take root, and scale."
  ]
};

export const authors = [
  {
    id: 1,
    name: "Craig Kimball",
    shortBio: "Craig has spent more than three decades transforming organisations from the inside out, across banking, insurance, logistics and digital marketplaces. He works as a CIO, CTO and board advisor, and was named LinkedIn Australia's #1 Influencer for Risk & Resilience in 2025.",
    fullBio: "Craig has spent three decades in senior leadership roles, living inside the complexity, the constraints, and dealing with the gap between what leaders think should happen and what actually needs to happen. His contribution to The Goldilocks Zone grew from that vantage point. It's an argument about why most transformation approaches treat symptoms instead of the underlying conditions that caused the need for a transformation in the first place, written by someone who spent years resolving those conditions from the inside. His work spans banking, insurance, logistics and digital marketplaces, always with a focus on the conditions that allow organisations to perform, adapt and sustain momentum across genuine transformation. He has led multi-disciplinary teams through technology modernisation, enterprise redesign and the adoption of emerging technologies, and has spent as much time on what organisations do to people as on what people do to organisations. Craig holds an honours degree in Chemical Engineering, an MBA, and is a graduate of the Australian Institute of Company Directors. His thinking has been further shaped by complexity science and leadership psychology. He works as a CIO, CTO, and board advisor, specialising in technology risk, transformation and operating model re-design.",
    role: "Co-Author"
  },
  {
    id: 2,
    name: "Nadir Khan",
    shortBio: "Nadir has spent more than two decades transforming organisations from the outside in, across telco, utilities, banking, insurance, cyber security and retail. He is the founder of Agilitie and a transformation advisor to boards, named one of The Australian Business Journal's '10 Speakers to Keep an Eye on in 2025.'",
    fullBio: "Nadir has spent more than two decades working across complex organisations, brought in from outside to see what proximity makes impossible to see, from the seat where the patterns that repeat across an industry come into view. That external vantage is where his contribution to The Goldilocks Zone was written from. It's an argument that the conditions a leader builds matter more than the playbook they run, written by someone who read those conditions from the outside, across one organisation after another. His work spans telco, utilities, banking, insurance, cyber security and retail, always centred on what makes change hold once the programme is over. He has led transformations for some of the largest companies across Australasia, Europe, the Middle East and the Americas, and has spent as much time on what lets change survive as on the mechanics that set it in motion. Nadir holds a master's degree in software engineering, with further study in organisational psychology, group dynamics and complexity theory. He works as an organisational transformation advisor and founder of Agilitie, advising boards and executive teams on the conditions that let large organisations keep evolving. Named one of The Australian Business Journal's 10 Speakers to Keep an Eye on in 2025, he regularly speaks and writes on how leaders turn change into a lasting capability.",
    role: "Co-Author"
  }
];

export const zones = [
  {
    title: "Cold Zone",
    description: "Organisations that are rigid, bureaucratic, risk-averse, and resistant to change. Decision-making is slow, innovation stalls, and people disengage.",
    icon: "Snowflake"
  },
  {
    title: "Hot Zone",
    description: "Organisations that are chaotic, reactive, and constantly firefighting. Teams are overwhelmed, clarity is absent, and long-term progress becomes impossible.",
    icon: "Flame"
  },
  {
    title: "Goldilocks Zone",
    description: "Balanced environments where structure and adaptability coexist, allowing meaningful change to succeed. Not too rigid, not too chaotic—just right.",
    icon: "Target"
  }
];

export const articles = [
  {
    id: 1,
    title: "Why Most Transformation Initiatives Fail",
    author: "Nadir Khan",
    date: "2025-01-15",
    summary: "Exploring the common pitfalls that cause organisational transformations to derail before they gain momentum.",
    content: `Most transformation initiatives fail not because of poor strategy, but because organisations haven't created the right environment for change to take hold. This article explores the fundamental reasons why transformation efforts falter and what leaders can do differently.`,
    category: "thought-leadership"
  },
  {
    id: 2,
    title: "The Power of Collective Intelligence in Decision-Making",
    author: "Craig Kimball",
    date: "2025-01-08",
    summary: "How diverse perspectives lead to better decisions and more resilient organisations.",
    content: `In an era of increasing complexity, no single leader has all the answers. This piece examines how organisations can harness collective intelligence to make better decisions and navigate uncertainty more effectively.`,
    category: "thought-leadership"
  },
  {
    id: 3,
    title: "Finding Balance: Structure Without Rigidity",
    author: "Nadir Khan & Craig Kimball",
    date: "2024-12-20",
    summary: "The art of creating organisational structures that provide direction without stifling innovation.",
    content: `The Goldilocks Zone isn't about eliminating structure—it's about finding the right kind of structure that enables rather than constrains. This article explores practical approaches to achieving this balance.`,
    category: "thought-leadership"
  }
];

export const media = [
  {
    id: 1,
    title: "The Future of Organisational Transformation",
    type: "Podcast",
    date: "2025-01-10",
    platform: "Leadership Matters Podcast",
    description: "Nadir Khan discusses the challenges facing modern organisations and the path to sustainable change.",
    url: "#"
  },
  {
    id: 2,
    title: "Keynote: Creating Hospitable Organisations",
    type: "Conference Talk",
    date: "2024-12-05",
    platform: "Global CIO Summit",
    description: "Craig Kimball's keynote on building organisational environments that support meaningful change.",
    url: "#"
  },
  {
    id: 3,
    title: "Interview: The Goldilocks Zone Approach",
    type: "Video",
    date: "2024-11-28",
    platform: "Business Insights TV",
    description: "Both authors discuss the core concepts behind their new book and what leaders can learn.",
    url: "#"
  }
];

export const events = [
  {
    id: 1,
    title: "Official Book Launch Event",
    date: "2025-03-15",
    location: "London, UK",
    type: "upcoming",
    description: "Join us for the official launch of The Goldilocks Zone. Meet the authors, engage in conversation, and discover how to create hospitable organisations.",
    registrationOpen: true
  },
  {
    id: 2,
    title: "Webinar: Applying the Goldilocks Principles",
    date: "2025-03-22",
    location: "Online",
    type: "upcoming",
    description: "A practical session exploring how to apply the book's principles in your organisation. Interactive Q&A with both authors.",
    registrationOpen: true
  },
  {
    id: 3,
    title: "Executive Roundtable Discussion",
    date: "2025-04-10",
    location: "New York, USA",
    type: "upcoming",
    description: "An intimate discussion for C-level executives on navigating organisational transformation in complex environments.",
    registrationOpen: true
  }
];

export const whyThisBook = {
  title: "Why This Book Exists",
  content: "Every year, organisations pour billions into transformation initiatives. The vast majority fail to deliver lasting results. Not because the strategies were flawed, or the people weren't talented enough, or the technology wasn't ready – but because nobody managed the environment the change had to survive in. Drop a brilliant transformation programme into a toxic, resource-starved, politically charged organisation and it will die. It doesn't matter how strong the strategy is – if the soil is hostile, nothing grows. This book addresses that fundamental gap. There are countless publications on what to change and how to change it. This book is about the physics behind the machinery – the first principles of creating organisations where meaningful transformation becomes possible, sustainable, and self-reinforcing."
};

export const whoThisBookIsFor = [
  "Senior executives navigating complex change",
  "C-level leaders responsible for transformation",
  "Transformation programme directors",
  "Organisational development practitioners",
  "Change leaders seeking sustainable approaches",
  "Anyone responsible for large-scale organisational change"
];

export const whatLeadersWillGain = {
  title: "What Leaders Will Gain",
  points: [
    "A diagnostic lens to see whether your organisation is running too hot, too cold, or in the zone where change can thrive.",
    "Practical strategies for cooling overheated organisations and thawing frozen ones – without the boom-and-bust cycle of failed transformations.",
    "Methods for unlocking collective intelligence – so your organisation stops making unintelligent decisions with intelligent people.",
    "The five behavioural reinforcement loops – alignment, gratitude, inclusion, forgiveness, and trust – that make transformation self-sustaining.",
    "Real-world case studies from organisations that found their Goldilocks Zone – and cautionary tales from those that didn't.",
    "A new paradigm for leadership: from driving change to becoming the custodian of an environment where change flourishes on its own."
  ]
};

export const purchaseLinks = [
  { platform: "Amazon", url: "#", available: true },
  { platform: "Bookshop.org", url: "#", available: true },
  { platform: "Direct Purchase", url: "#", available: true }
];

export const socialMedia = [
  { platform: "LinkedIn", url: "#", icon: "Linkedin" },
  { platform: "Twitter", url: "#", icon: "Twitter" },
  { platform: "YouTube", url: "#", icon: "Youtube" }
];
